import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Stat } from '@/components/ui/Stat';
import { Spark } from '@/components/ui/Chart';
import { Icon } from '@/components/ui/Icon';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SoftphonePanel } from '../components/SoftphonePanel';
import { SoftphoneLayout } from '@/components/layout/SoftphoneLayout';
import { agentApi } from '@/shared/api/agentApi';

interface Badge { code: string; label_he: string; label_en: string }
interface StatsData {
  today?: { calls: number; answered: number; total_talk_sec: number; aht_sec: number };
  week_chart?: { day: string; count: number }[];
  badges?: Badge[];
}

const WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

export function Softphone() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [links, setLinks] = useState<{ payment?: string | null; calendar?: string | null }>({});

  // NOTE: SIP registration is owned by AgentLayout, which fetches real
  // credentials first and calls register(creds). This page must NOT call
  // register() with no args — that produced "incomplete credentials: no
  // credentials" and clobbered the real registration into an error state.

  useEffect(() => {
    void (async () => {
      try {
        const s = await agentApi.stats();
        setStats(s as StatsData);
      } catch {
        setStats(null);
      }
    })();
    // Quick-action links the business configured (payment / calendar).
    void agentApi.settings()
      .then((d) => setLinks((d as { links?: { payment?: string | null; calendar?: string | null } })?.links ?? {}))
      .catch(() => setLinks({}));
  }, []);

  const weekValues = stats?.week_chart?.map((d) => d.count) ?? [0, 0, 0, 0, 0, 0, 0];

  return (
    <SoftphoneLayout
      softphone={<SoftphonePanel />}
      content={
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--gap)' }}>
          <Stat
            label="שיחות היום"
            value={stats?.today?.calls ?? 0}
            icon={<Icon name="phone" size={16} color="var(--accent)" />}
          />
          <Stat
            label="נענו"
            value={stats?.today?.answered ?? 0}
            icon={<Icon name="ok" size={16} color="var(--ok)" />}
          />
          <Stat
            label="זמן דיבור"
            value={fmtDur(stats?.today?.total_talk_sec ?? 0)}
            icon={<Icon name="clock" size={16} color="var(--info)" />}
          />
          <Stat
            label="AHT ממוצע"
            value={fmtDur(stats?.today?.aht_sec ?? 0)}
            icon={<Icon name="chart" size={16} color="var(--busy)" />}
          />
        </div>

        <Card title="ביצועים השבוע">
          <Spark values={weekValues.length ? weekValues : [0]} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            {(stats?.week_chart ?? []).map((d) => (
              <div key={d.day} style={{ flex: 1, textAlign: 'center', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                <div>{WEEKDAYS[new Date(d.day).getDay()]}</div>
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>{d.count}</div>
              </div>
            ))}
          </div>
        </Card>

        {(stats?.badges?.length ?? 0) > 0 && (
          <Card title="הישגים">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {stats!.badges!.map((b) => (
                <StatusBadge key={b.code} tone="accent">
                  <Icon name="star" size={12} /> {b.label_he}
                </StatusBadge>
              ))}
            </div>
          </Card>
        )}

        <Card title="פעולות מהירות">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <Link to="/agent/callbacks" className="btn">
              <Icon name="clock" size={16} /> חזרות מתוכננות
            </Link>
            <Link to="/agent/contacts" className="btn">
              <Icon name="users" size={16} /> אנשי קשר
            </Link>
            <Link to="/agent/history" className="btn">
              <Icon name="history" size={16} /> היסטוריה
            </Link>
            {/* Business-configured links — only render when set (admin → הגדרות → חברה). */}
            {links.payment && (
              <a href={links.payment} target="_blank" rel="noopener noreferrer" className="btn">
                <Icon name="creditCard" size={16} /> קישור סליקה
              </a>
            )}
            {links.calendar && (
              <a href={links.calendar} target="_blank" rel="noopener noreferrer" className="btn">
                <Icon name="calendar" size={16} /> יומן
              </a>
            )}
          </div>
        </Card>
      </>
      }
    />
  );
}

function fmtDur(s: number): string {
  if (!s) return '00:00';
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
